<template>
    <section>
        <ScheduleXCalendar v-if="calendarOptionsLoaded" :calendar-app="calendarApp" />
    </section>
</template>

<script>
import { markRaw } from 'vue';
import { ScheduleXCalendar } from '@schedule-x/vue'
import { createCalendar, createViewMonthAgenda } from '@schedule-x/calendar';
import '@schedule-x/theme-default/dist/index.css';
import { parse, formatISO, add, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { nextTick } from 'vue';

export default {
    name: 'CalendarMonth',
    emits: ['update:calendarRange', 'day-dblclick'],
    props: {
        calendarEvents: Array,
    },
    components: {
        ScheduleXCalendar,
    },
    data() {
        return {
            isStaff: this.$store.state.accessLevel == 'admin' || this.$store.state.accessLevel == 'secretary',
            
            calendarOptionsLoaded: false,
            calendarApp: null
        }
    },
    methods: {
                highlightBlockedDayCells() {
            try {
                const blocked = (this.calendarEvents || []).filter(e => e.isBlocked);
                if (!blocked.length) return;

                const labels = new Set(
                    blocked.map(e => {
                        const d = parse(e.date, 'dd/MM/yyyy', new Date());
                        return format(d, "d 'de' MMMM 'de' yyyy", { locale: ptBR });
                    })
                );

                const dayButtons = document.querySelectorAll('button[aria-label]');
                dayButtons.forEach(btn => {
                    const label = btn.getAttribute('aria-label');
                    if (labels.has(label)) {
                        btn.classList.add('blocked-day-cell');
                    }
                    // Never remove the class here; keep lock persistent
                });
            } catch (err) {
                console.warn('highlightBlockedDayCells error', err);
            }
        }
    },
    computed: {
        convertedEvents() {
            return this.calendarEvents.map(event => {
                const parsedDate = parse(event.date, 'dd/MM/yyyy', new Date());
                const formattedDate = formatISO(parsedDate, { representation: 'date' });

                let start, end;
                
                if (event.isBlocked) {
                    // Blocked dates span the entire day: 00:00 to 23:59
                    start = `${formattedDate} 00:00`;
                    end = `${formattedDate} 23:59`;
                } else {
                    // Regular events span 1 hour
                    const startTime = parse(event.hour, 'HH:mm', new Date());
                    const endTime = add(startTime, { hours: 1 });
                    start = `${formattedDate} ${event.hour}`;
                    end = `${formattedDate} ${format(endTime, 'HH:mm')}`;
                }

                // Blocked dates use a special calendar ID
                const calendarId = event.isBlocked ? 'blocked' : 'room' + event.roomId;

                return {
                    id: event.id,
                    title: event.title || event.author,
                    start: start,
                    end: end,
                    calendarId: calendarId,
                };
            });
        },
    },
    mounted() {
        const urlPrefix = this.isStaff ? '/admin' : '/user';
        
        // We only set the options when computed() works, can't do on data()
        // Uses markRaw to avoid deep reactivity
        this.calendarApp = markRaw(
            createCalendar({
                selectedDate: new Date().toISOString().slice(0, 10),
                views: [createViewMonthAgenda()],
                locale: 'pt-BR',
                // TODO: Revisar salas fixas?
                calendars: {
                    blocked: {
                        colorName: 'blocked',
                        lightColors: {
                            main: '#ef4444', // Red for blocked days
                            container: '#fee2e2', // Light red background
                            onContainer: '#7f1d1d', // Dark red text
                        },
                    },
                    room1: {
                        colorName: 'room1',
                        lightColors: {
                            main: '#05ff26', // Lateral e cor geral
                            container: '#f9f6eb', // Cor de fundo (padrão)
                            onContainer: '#382e27', // Cor de texto (padrão)
                        },
                    },
                    room2: {
                        colorName: 'room2',
                        lightColors: {
                            main: '#960ef0', // Lateral e cor geral
                            container: '#f9f6eb',
                            onContainer: '#382e27',
                        },
                    },
                    room3: {
                        colorName: 'room3',
                        lightColors: {
                            main: '#ffbb00', // Lateral e cor geral
                            container: '#f9f6eb',
                            onContainer: '#382e27',
                        },
                    },
                },
                events: this.convertedEvents,
                callbacks: {
                    onDoubleClickEvent: (event) => {
                        this.makeRedirect(`${urlPrefix}/agendas/editar/` + event.id);
                    },
                    onDoubleClickAgendaDate: (dateStr) => {
                        this.$emit('day-dblclick', dateStr);
                    },
                    onRangeUpdate: (range) => {
                        this.$emit('update:calendarRange', range);
                    }
                }
            })
        )

        // We build the calendar on the screen only when this.calendarApp are configured to avoid issues
        this.calendarOptionsLoaded = true;

        // Re-apply blocked classes on user interactions
        this.$nextTick(() => {
            const root = this.$el;
            if (root) {
                // Clicks inside the calendar can toggle classes; re-apply after clicks
                root.addEventListener('click', () => {
                    setTimeout(() => this.highlightBlockedDayCells(), 0);
                }, true);

                // Observe DOM mutations to keep the lock persistent
                const observer = new MutationObserver(() => {
                    this.highlightBlockedDayCells();
                });
                observer.observe(root, { childList: true, subtree: true });
                this._blockedObserver = observer;
            }
        });
    },
    beforeUnmount() {
        if (this._blockedObserver) {
            try { this._blockedObserver.disconnect(); } catch (e) {}
            this._blockedObserver = null;
        }
    },
    watch: {
        calendarEvents: {
            handler(newEvents) {
                if (this.calendarApp) {
                    // Update the calendar events dynamically
                    this.calendarApp.events.set(this.convertedEvents);
                    nextTick(() => {
                        this.highlightBlockedDayCells();
                    });
                }
            },
            deep: true
        }
    }
}
</script>
<style scoped>
:deep(.blocked-day-cell) {
    position: relative;
    border: 1px solid #ef4444 !important;
}

/* Small lock icon on top-right for blocked cells */
:deep(.blocked-day-cell)::after {
    content: '🔒';
    position: absolute;
    top: 6px;
    right: 6px;
    font-size: 16px;
    line-height: 1;
    opacity: 0.85;
}
</style>